import { MAP_ICON_URL } from '@/constants/icons';
import { MY_LOCATION_ACTIVE_ID, MY_LOCATION_ANIMATE, MY_LOCATION_MARKER } from '@/constants/floor-guide';
import type { MachineInfo } from '@/services/dto';
import { useMarkerStore } from '@/stores/markerStore';
import type { PoiItem } from '@/types/map';
import type { DabeeoMap } from 'dabeeomaps';
import type { IMarkerOption } from 'dabeeomaps';
import type { IMyLocOption } from 'dabeeomaps/dist/src/model/map/IMyLocOption';

export const mapPoiToMarker = (
    poiItem?: PoiItem,
    iconSize: { width: number; height: number } = { width: 35, height: 48 },
    iconUrl?: string
): IMarkerOption | undefined => {
    if (!poiItem) {
        return undefined;
    }
    return {
        x: poiItem?.position?.x,
        y: poiItem?.position?.y,
        floorId: poiItem?.floorId,
        fixedSize: true,
        iconOption: {
            width: iconSize?.width,
            height: iconSize?.height,
            anchor: {
                x: 0.5,
                y: -0.15,
            },
            iconUrl: iconUrl || MAP_ICON_URL.POSITION_MARKER_ICON,
        },
    };
};

export const mapPoiListToMarker = (
    poiList?: PoiItem[],
    iconSize: { width: number; height: number } = { width: 35, height: 48 },
    iconUrl?: string
): IMarkerOption[] => {
    if (!Array.isArray(poiList) || !poiList?.length) {
        return [];
    }

    const result: IMarkerOption[] = poiList.map((poi) => ({
        x: poi?.position?.x,
        y: poi?.position?.y,
        fixedSize: true,
        floorId: poi?.floorId,
        iconOption: {
            width: iconSize?.width,
            height: iconSize?.height,
            anchor: {
                x: 0.5,
                y: -0.15,
            },
            iconUrl: iconUrl || MAP_ICON_URL.DESTINATION_POINT_ICON,
        },
    }));

    return result;
};

const getMyLocationPinAnchorY = (): number => {
    return MY_LOCATION_MARKER.PIN_TIP_Y / MY_LOCATION_MARKER.BASE_SIZE.height;
};

export const calculateMyLocationPulseIconSize = (
    pulseScreenSize: { width: number; height: number }
): { width: number; height: number } => {
    return {
        width: Math.round(pulseScreenSize.width),
        height: Math.round(pulseScreenSize.height),
    };
};

export const mapMyLocationToMarker = (
    floorId?: string,
    myLocation?: MachineInfo,
    iconSize: { width: number; height: number } = { width: 35, height: 48 }
): IMarkerOption | undefined => {
    if (!myLocation) {
        return undefined;
    }
    return {
        x: myLocation.positionX,
        y: myLocation.positionY,
        floorId,
        fixedSize: true,
        iconOption: {
            width: iconSize.width,
            height: iconSize.height,
            anchor: {
                x: 0.5,
                y: 0.55,
            },
            iconUrl: MAP_ICON_URL.CURRENT_LOCATION_LEGACY_ICON,
        },
    };
};

export const mapMyLocationPinMarker = (
    floorId: string,
    myLocation: MachineInfo,
    iconSize: { width: number; height: number }
): IMarkerOption => {
    return {
        x: myLocation.positionX,
        y: myLocation.positionY,
        floorId,
        fixedSize: true,
        iconOption: {
            width: iconSize.width,
            height: iconSize.height,
            anchor: {
                x: 0.5,
                y: getMyLocationPinAnchorY(),
            },
            iconUrl: MAP_ICON_URL.CURRENT_LOCATION_ICON,
        },
    };
};

export const mapMyLocationPulseMyLocOption = (
    floorId: string,
    myLocation: MachineInfo,
    pulseIconSize: { width: number; height: number }
): IMyLocOption => {
    return {
        x: myLocation.positionX,
        y: myLocation.positionY,
        floorId,
        onActive: true,
        iconOption: {
            width: pulseIconSize.width,
            height: pulseIconSize.height,
            anchor: {
                x: 0.5,
                y: 0.5,
            },
            iconUrl: MAP_ICON_URL.CURRENT_LOCATION_PULSE_PLACEHOLDER,
        },
        animate: {
            color: MY_LOCATION_ANIMATE.COLOR,
            opacity: MY_LOCATION_ANIMATE.OPACITY,
            desireScale: MY_LOCATION_ANIMATE.DESIRE_SCALE,
            duration: MY_LOCATION_ANIMATE.DURATION,
            repeat: MY_LOCATION_ANIMATE.REPEAT,
        },
    };
};

export const clearMyLocationFromMap = (currentMap?: DabeeoMap): void => {
    if (!currentMap) {
        return;
    }
    currentMap.mylocation?.clear?.();
    const { myLocation } = useMarkerStore.getState().markerIds;
    if (myLocation) {
        currentMap.markers?.clear?.([myLocation]);
    }
    useMarkerStore.getState().setMarkerIds({ myLocation: undefined, myLocationPulse: undefined });
};

export { MY_LOCATION_ACTIVE_ID };
